import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { Copy, Download, Eye, History, ImageOff, Pencil, PlusCircle, Trash2 } from 'lucide-react'
import LoadMoreButton from '../../../../components/LoadMoreButton'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import GeneratePDFModal from '../../../../components/Modals/FashionProduct/GeneratePDFModal'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import { useOutletContext, useNavigate } from 'react-router-dom'
import axios from '../../../../config/axios'
import AttachmentApiService from '../../../../services/AttachmentApiService'
import { StandardDataTable, useColumnsState, OrdenoLoading, RowActionsMenu, AuditHistoryModal, RowAction } from '../../../../components/StandardPage'
import TagComponent from '../../../../components/ui/Tag'
import {
  buildCompositionColumns,
  compositionScreenKey,
  ProductListItem,
} from './compositionColumns'

const InfoMessage = styled.div`
  padding: 60px 40px;
  text-align: center;
  color: #999;
  font-size: 16px;
  background-color: white;
  border-radius: 8px;
`

const BoardWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 10px;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #c0c0c0;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #ffffff;
    border-radius: 10px;
    border: 2px solid #c0c0c0;
  }
`

const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
const API_BASE_URL = `${GATEWAY_URL}/backend-fashion-orders/fashion-product`

const BOARD_COVER_PALETTE = ['#EDE3B9', '#D9D5EA', '#CBE9DD', '#EFD7D9', '#D0E3F2']
const IMAGE_ATTACHMENT_EXTENSION_REGEX =
  /\.(png|jpe?g|gif|bmp|webp|svg|avif|heic|heif|jfif)$/i

interface Grade {
  id: number
  name: string
  typeId: number
  GraduationType?: {
    id: number
    name: string
  }
}

interface GradeInProduct {
  id: number
  productId: number
  gradeId: number
  Grade: Grade
}

interface Group {
  id: number
  name: string
  tenantId: number
}

interface Family {
  id: number
  name: string
  collectionId: number
  tenantId: number
}

interface ProductResponse {
  id: number
  norm: string
  name: string
  productCode: string
  referenceProduct?: string | null
  description?: string | null
  price?: number | null
  finalPrice?: number | null
  supplierCode?: string | null
  observations?: string | null
  clientIdentification?: string | null
  collectionId: number
  familyId: number
  groupId: number
  createdAt?: string
  updatedAt?: string
  status?: boolean
  Group?: Group
  Family?: Family
  Collection?: {
    id: number
    name: string
    tenantId: number
  }
  Episode?: {
    id: number
    name: string
  }
  GradeInProduct?: {
    id: number
    productId: number
    gradeId: number
    Grade?: {
      id: number
      name: string
      GraduationType?: {
        id: number
        name: string
      }
    }
  }[]
  Script?: {
    id: number
    name: string
    ScriptMaterial?: {
      id: number
      idealMeasurements?: string | null
      Material?: { id: number; name: string } | null
      Stock?: {
        id: number
        description?: string | null
        Code?: { code?: string } | null
      } | null
    }[]
  }[]
  ProductApproval?: {
    id: number
    name: string
    type?: string
    status?: string
  }[]
  ProductMeasure?: {
    id: number
    value?: number | null
    tolerance?: number | null
    MeasureType?: { id: number; name: string } | null
    Grade?: { id: number; name: string } | null
  }[]
  BeneficiamentoNote?: {
    id: number
    description?: string | null
    Sector?: { id: number; name: string } | null
  }[]
  CoverGeneralFile?: { fileUrl?: string } | null
  coverImageScale?: number | null
  coverImagePosX?: number | null
  coverImagePosY?: number | null
  attachments?: string[]
}

type ProductListItemDraft = ProductListItem & { coverSourcePath: string | null }

const isImageAttachmentUrl = (url: string) => {
  const nameFromUrl = url.split('/').pop() || url
  try {
    return IMAGE_ATTACHMENT_EXTENSION_REGEX.test(decodeURIComponent(nameFromUrl))
  } catch {
    return IMAGE_ATTACHMENT_EXTENSION_REGEX.test(nameFromUrl)
  }
}

const resolveCoverSource = (product: ProductResponse): string | null => {
  const coverFileUrl = product.CoverGeneralFile?.fileUrl
  if (coverFileUrl) return coverFileUrl

  const attachments = product.attachments || []
  const firstImage = attachments.find((url) => isImageAttachmentUrl(url))
  return firstImage || null
}

const getFashionCoverSignedUrl = async (
  filePath: string,
  token: string,
): Promise<string | null> => {
  if (!filePath) return null

  try {
    const url = await AttachmentApiService.getSignedUrl(filePath)
    return url || null
  } catch {
    return null
  }
}

const BoardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
  padding-right: 10px;
`

const BoardCard = styled.div`
  background: #f7f7f7;
  border: 1px solid #efefef;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
`

const CreateBoardCard = styled.button`
  min-height: 262px;
  border: 2px dashed #d5d5d5;
  border-radius: 16px;
  background: #f1f1f1;
  color: #8a8a8a;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;

  span {
    font-size: 16px;
    font-weight: 700;
  }

  &:hover {
    border-color: #bfbfbf;
    background: #ececec;
    color: #767676;
  }
`

const BoardCoverArea = styled.div<{ $background: string }>`
  height: 140px;
  background: ${(props) => props.$background};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
`

const BoardCoverImage = styled.img<{ $scale?: number | null; $posX?: number | null; $posY?: number | null }>`
  width: 100%;
  height: 100%;
  object-fit: contain;
  transform-origin: ${(props) => props.$posX ?? 50}% ${(props) => props.$posY ?? 50}%;
  transform: scale(${(props) => props.$scale ?? 1});
`

const BoardCoverFallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #7a7a7a;

  span {
    font-size: 12px;
    font-weight: 600;
  }
`

const BoardInfo = styled.div`
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const BoardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 28px;
`

const BoardCode = styled.span`
  color: #8d8d8d;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`

const BoardTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #101010;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const BoardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 6px;
`

const TagBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const CollectionTagBadge = styled(TagBadge)`
  color: #4a2d7a;
  background: #f3eefb;
`

const EpisodeTagBadge = styled(TagBadge)`
  color: #1e5c3a;
  background: #e8f5ee;
`

const FamilyTagBadge = styled(TagBadge)`
  color: #0f3f59;
  background: #eef6fb;
`

const CardSeparator = styled.div`
  height: 1px;
  background: #e8e8e8;
  margin: 4px 0;
  width: 100%;
`

const CardInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const CardStepRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`

const CardStepLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #8d8d8d;
`

const CardStepBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 6px;
  background-color: #e6f4fb;
  color: #0090d3;
  font-size: 11px;
  font-weight: 700;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const CardTagsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`

const CompositionTable: React.FC = () => {
  const contextOutlet = useOutletContext<any>()
  const refresh = contextOutlet?.refresh
  const search = contextOutlet?.search
  const viewMode = contextOutlet?.viewMode
  const appliedFilters = contextOutlet?.appliedFilters || []
  const appliedFiltersStr = useMemo(() => JSON.stringify(appliedFilters), [appliedFilters])

  const filterColecao = appliedFilters?.find((f: any) => f.key === 'colecao')?.value
  const filterFamilia = appliedFilters?.find((f: any) => f.key === 'familia')?.value
  const filterEpisodio = appliedFilters?.find((f: any) => f.key === 'episodio')?.value

  const navigate = useNavigate()

  const [products, setProducts] = useState<ProductListItem[]>([])
  const [visibleCount, setVisibleCount] = useState<number>(50)
  const [totalProducts, setTotalProducts] = useState<number | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const [deleteCandidate, setDeleteCandidate] = useState<{
    id: number
    name: string
  } | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false)
  const [pdfProductId, setPdfProductId] = useState<number | null>(null)
  const [coverPreview, setCoverPreview] = useState<{
    url: string
    name: string
    left: number
    top: number
  } | null>(null)
  const productTicketMapRef = useRef<
    Record<
      number,
      {
        stepName: string
        tags: Array<{ id: number; name: string; color?: string }>
      }
    >
  >({})

  const { setFeedback } = useContext(FeedbackContext)

  const columns = useMemo(() => buildCompositionColumns(), [])
  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(
    compositionScreenKey,
    columns,
  )

  useEffect(() => {
    setVisibleCount(50)
  }, [filterColecao, filterFamilia, filterEpisodio])

  const matchStringCondition = useCallback((text: string, filterVal: string, filterType: string): boolean => {
    const t = (text || '').toLowerCase().trim()
    const f = (filterVal || '').toLowerCase().trim()
    if (!f && filterType !== 'Diferente de' && filterType !== 'É diferente de' && filterType !== 'notEquals') return true
    switch (filterType) {
      case 'Igual a':
      case 'É igual a':
      case 'equals':
        return t === f
      case 'Diferente de':
      case 'É diferente de':
      case 'notEquals':
        return t !== f
      case 'Começa com':
      case 'startsWith':
        return t.startsWith(f)
      case 'Termina com':
      case 'endsWith':
        return t.endsWith(f)
      case 'Não contém':
      case 'notContains':
        return !t.includes(f)
      case 'Contém':
      case 'contains':
      default:
        return t.includes(f)
    }
  }, [])

  const fetchProductTickets = useCallback(async () => {
    const token = localStorage.getItem('token')
    if (!token) return {}

    try {
      let pipeline: any = null

      try {
        const getPipelineRes = await axios.get(
          `${GATEWAY_URL}/pipelines/pipeline`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { moduleType: 'product_development', subModule: 'fashion' },
          },
        )
        const list = getPipelineRes.data?.data || getPipelineRes.data || []
        if (Array.isArray(list) && list.length > 0) {
          pipeline = list[0]
        }
      } catch (e) {
        console.warn('Erro ao buscar pipeline por GET:', e)
      }

      if (!pipeline?.id) {
        const pipelineResponse = await axios.post(
          `${GATEWAY_URL}/pipelines/pipeline/setup-module`,
          {
            moduleType: 'product_development',
            subModule: 'fashion',
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        )
        const pipelineData = pipelineResponse.data?.data || pipelineResponse.data
        pipeline = Array.isArray(pipelineData) ? pipelineData[0] : pipelineData
      }

      if (!pipeline?.id) return {}

      const stepsMap = new Map<number, string>()
      try {
        const stepsResponse = await axios.get(
          `${GATEWAY_URL}/pipelines/step`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { pipelineId: pipeline.id },
          },
        )
        const stepsList = Array.isArray(stepsResponse.data?.data)
          ? stepsResponse.data.data
          : Array.isArray(stepsResponse.data)
          ? stepsResponse.data
          : []
        stepsList.forEach((step: any) => {
          if (step.id && step.name) stepsMap.set(step.id, step.name)
        })
      } catch (stepErr) {
        console.warn('Erro ao buscar steps do pipeline:', stepErr)
      }
      if (stepsMap.size === 0 && Array.isArray(pipeline.Step)) {
        pipeline.Step.forEach((step: any) => {
          if (step.id && step.name) stepsMap.set(step.id, step.name)
        })
      }

      let ticketsResponse = await axios.get(
        `${GATEWAY_URL}/pipelines/ticket`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { pipelineId: pipeline.id, take: 1000 },
        },
      )

      let ticketList: any[] = Array.isArray(ticketsResponse.data?.data)
        ? ticketsResponse.data.data
        : Array.isArray(ticketsResponse.data)
        ? ticketsResponse.data
        : Array.isArray(ticketsResponse.data?.tickets)
        ? ticketsResponse.data.tickets
        : []

      if (ticketList.length === 0) {
        try {
          await axios.post(
            `${GATEWAY_URL}/pipelines/ticket/sync-module`,
            {
              moduleType: 'product_development',
              pipelineId: pipeline.id,
              subModule: 'fashion',
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          )
          ticketsResponse = await axios.get(
            `${GATEWAY_URL}/pipelines/ticket`,
            {
              headers: { Authorization: `Bearer ${token}` },
              params: { pipelineId: pipeline.id, take: 1000 },
            },
          )
          ticketList = Array.isArray(ticketsResponse.data?.data)
            ? ticketsResponse.data.data
            : Array.isArray(ticketsResponse.data)
            ? ticketsResponse.data
            : Array.isArray(ticketsResponse.data?.tickets)
            ? ticketsResponse.data.tickets
            : []
        } catch (syncErr) {
          console.warn('Erro ao sincronizar módulo de desenvolvimento:', syncErr)
        }
      }

      const map: Record<
        number,
        {
          stepName: string
          tags: Array<{ id: number; name: string; color?: string }>
        }
      > = {}

      for (const ticket of ticketList) {
        if (ticket.parentTicketId) continue
        const productId =
          typeof ticket.externalId === 'number' && ticket.externalId > 0
            ? ticket.externalId
            : typeof ticket.externalData?.productId === 'number'
            ? ticket.externalData.productId
            : null

        if (!productId) continue

        const stepName = ticket.Step?.name || stepsMap.get(ticket.stepId) || ''
        const rawTags = Array.isArray(ticket.TagTicket)
          ? ticket.TagTicket.map((tt: any) => tt.Tag || tt)
          : Array.isArray(ticket.tags)
          ? ticket.tags.map((t: any) => t.tag || t)
          : []

        const tags: Array<{ id: number; name: string; color?: string }> = rawTags
          .filter((t: any) => t && t.name)
          .map((t: any) => ({
            id: t.id,
            name: t.name,
            color: t.color,
          }))

        map[productId] = { stepName, tags }
      }

      productTicketMapRef.current = map
      return map
    } catch (err) {
      console.error('Erro ao buscar tickets do pipeline de desenvolvimento:', err)
      return {}
    }
  }, [])

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    const token = localStorage.getItem('token')
    if (!token) {
      setError('Sessão expirada. Por favor, faça login novamente.')
      setLoading(false)
      navigate('/login')
      return
    }

    try {
      let currentTicketMap = productTicketMapRef.current
      if (Object.keys(currentTicketMap).length === 0) {
        const fetched = await fetchProductTickets()
        if (fetched && Object.keys(fetched).length > 0) {
          currentTicketMap = fetched
        }
      }

      let searchProductIds: number[] | undefined = undefined
      if (search && search.trim()) {
        const s = search.trim().toLowerCase()
        const matched = Object.entries(currentTicketMap)
          .filter(([_, data]: [string, any]) => {
            const stepMatch = data.stepName && data.stepName.toLowerCase().includes(s)
            const tagMatch = data.tags && data.tags.some((t: any) => t.name && t.name.toLowerCase().includes(s))
            return stepMatch || tagMatch
          })
          .map(([pId]) => Number(pId))
        if (matched.length > 0) {
          searchProductIds = matched
        }
      }

      let parsedFilters: any[] = []
      try {
        parsedFilters = JSON.parse(appliedFiltersStr)
      } catch {
        parsedFilters = []
      }

      let processedFilters = parsedFilters
      if (parsedFilters && parsedFilters.length > 0) {
        processedFilters = parsedFilters.map((f: any) => {
          if (f.key === 'step' || f.key === 'etapa') {
            const matchedIds = Object.entries(currentTicketMap)
              .filter(([_, data]: [string, any]) => {
                const stepName = data.stepName || ''
                return matchStringCondition(stepName, String(f.value || ''), f.filterType)
              })
              .map(([pId]) => Number(pId))

            return {
              ...f,
              productIds: matchedIds.length > 0 ? matchedIds : [-1],
            }
          }
          return f
        })
      }

      const response = await axios.get(`${API_BASE_URL}/paginated`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          page: 0,
          size: visibleCount,
          search: search || undefined,
          searchProductIds: searchProductIds ? searchProductIds.join(',') : undefined,
          colecao: filterColecao || undefined,
          familia: filterFamilia || undefined,
          episodio: filterEpisodio || undefined,
          filters: processedFilters && processedFilters.length > 0 ? JSON.stringify(processedFilters) : undefined,
        },
      })

      const data = response.data
      let productsData: ProductResponse[] = []
      if (Array.isArray(data)) {
        productsData = data
        setTotalProducts(null)
      } else if (data && Array.isArray(data.content)) {
        productsData = data.content
        setTotalProducts(typeof data.totalElements === 'number' ? data.totalElements : null)
      }

      const transformedProducts: ProductListItemDraft[] = productsData.map(
        (product) => {
          const collection = product.Collection?.name || '-'
          const episode = product.Episode?.name || '-'
          const family = product.Family?.name || '-'

          const grades = product.GradeInProduct?.map(gp => gp.Grade?.name).join(', ') || '-'
          const groups = product.Group?.name || '-'
          const colorSet = new Set<string>()
          const pics = (product as any).ProductItemColor || []
          for (const pic of pics) {
            const cc = pic.CollectionColor || pic.collectionColor
            if (cc && cc.name) colorSet.add(cc.name)
          }
          const colors = colorSet.size > 0 ? Array.from(colorSet).join(', ') : '-'

          const graduationTypes = Array.from(
            new Set(
              (product.GradeInProduct || [])
                .map((gp) => gp.Grade?.GraduationType?.name)
                .filter(Boolean),
            ),
          ).join(', ') || '-'

          const materialsSet = new Set<string>()
          for (const s of product.Script || []) {
            for (const sm of s.ScriptMaterial || []) {
              const matName = sm.Material?.name || sm.Stock?.description || sm.Stock?.Code?.code
              if (matName) materialsSet.add(matName)
            }
          }
          const composition = materialsSet.size > 0 ? Array.from(materialsSet).join(', ') : '-'

          const annotation = (product.BeneficiamentoNote || [])
            .map((bn) => bn.description)
            .filter(Boolean)
            .join(', ') || '-'

          const approvals = (product.ProductApproval || [])
            .map((pa) => (pa.status ? `${pa.name} (${pa.status})` : pa.name))
            .filter(Boolean)
            .join(', ') || '-'

          const measures = (product.ProductMeasure || [])
            .map((pm) => (pm.MeasureType?.name ? `${pm.MeasureType.name}: ${pm.value ?? '-'}` : null))
            .filter(Boolean)
            .join(', ') || '-'

          const step = currentTicketMap[product.id]?.stepName || productTicketMapRef.current[product.id]?.stepName || '-'
          const tags = currentTicketMap[product.id]?.tags || productTicketMapRef.current[product.id]?.tags || []

          return {
            id: product.id,
            code: product.productCode || product.norm || '-',
            name: product.name || '-',
            referenceProduct: product.referenceProduct || '-',
            description: product.description || '-',
            price: product.price ?? product.finalPrice ?? null,
            graduationType: graduationTypes,
            supplierCode: product.supplierCode || '-',
            observations: product.observations || '-',
            clientIdentification: product.clientIdentification || '-',
            composition,
            annotation,
            approvals,
            measures,
            step,
            tags,
            collection,
            episode,
            family,
            grades,
            groups,
            colors,
            coverImageUrl: null,
            coverImageScale: typeof product.coverImageScale === 'number' ? product.coverImageScale : null,
            coverImagePosX: typeof product.coverImagePosX === 'number' ? product.coverImagePosX : null,
            coverImagePosY: typeof product.coverImagePosY === 'number' ? product.coverImagePosY : null,
            coverSourcePath: resolveCoverSource(product),
          }
        },
      )

      const productsWithCover: ProductListItem[] =
        viewMode === 'board'
          ? await Promise.all(
              transformedProducts.map(async (item) => {
                const { coverSourcePath, ...rest } = item
                if (!coverSourcePath) {
                  return rest
                }

                const coverImageUrl =
                  (await getFashionCoverSignedUrl(coverSourcePath, token)) ||
                  coverSourcePath

                return {
                  ...rest,
                  coverImageUrl,
                }
              }),
            )
          : transformedProducts.map(({ coverSourcePath: _coverSourcePath, ...rest }) => rest)

      setProducts(productsWithCover)
    } catch (err: unknown) {
      console.error('Erro ao buscar produtos:', err)
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          setError('Não autorizado. Por favor, faça login novamente.')
          navigate('/login')
        } else {
          setError(
            err.response?.data?.message ||
              'Falha ao carregar produtos. Tente novamente mais tarde.',
          )
        }
      } else {
        setError('Erro inesperado ao carregar produtos.')
      }
    } finally {
      setLoading(false)
    }
  }, [
    visibleCount,
    search,
    filterColecao,
    filterFamilia,
    filterEpisodio,
    viewMode,
    navigate,
    appliedFiltersStr,
    fetchProductTickets,
    matchStringCondition,
  ])

  useEffect(() => {
    let isCancelled = false
    const load = async () => {
      await fetchProductTickets()
      if (!isCancelled) {
        await fetchData()
      }
    }
    void load()
    return () => {
      isCancelled = true
    }
  }, [fetchData, fetchProductTickets, refresh])

  const [auditModalData, setAuditModalData] = useState<{
    tableName: string
    recordId: number
    recordTitle?: string
  } | null>(null)

  const handleEdit = useCallback((productId: number) => {
    navigate(`/panel/composition-product/edit/${productId}`)
  }, [navigate])

  const handleCreateVariation = useCallback((productId: number) => {
    navigate(`/panel/composition-product/create?variationFrom=${productId}`)
  }, [navigate])

  const handleDelete = (productId: number) => {
    const product = products.find((p) => p.id === productId)
    const productName = product ? product.name : productId

    setDeleteCandidate({ id: productId, name: String(productName) })
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    if (!deleteCandidate) return

    const token = localStorage.getItem('token')
    if (!token) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Sessão expirada. Por favor, faça login novamente.',
      })
      navigate('/login')
      return
    }

    try {
      setLoading(true)
      setError(null)
      await axios.delete(`${API_BASE_URL}/${deleteCandidate.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      setProducts((prevProducts) =>
        prevProducts.filter((product) => product.id !== deleteCandidate.id),
      )

      setFeedback({
        isOpen: true,
        statusOk: true,
        message: 'Produto excluído com sucesso!',
      })
    } catch (err: unknown) {
      console.error(`Erro ao excluir produto ${deleteCandidate.id}:`, err)
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          setError(
            'Não autorizado para excluir. Por favor, faça login novamente.',
          )
          setFeedback({
            isOpen: true,
            statusOk: false,
            message: 'Não autorizado para excluir. Por favor, faça login novamente.',
          })
          navigate('/login')
        } else {
          const errorMessage =
            err.response?.data?.message || 'Falha ao excluir o produto.'
          setError(errorMessage)
          setFeedback({
            isOpen: true,
            statusOk: false,
            message: errorMessage,
          })
        }
      } else {
        setError('Erro inesperado ao excluir produto.')
        setFeedback({
          isOpen: true,
          statusOk: false,
          message: 'Erro inesperado ao excluir produto.',
        })
      }
    } finally {
      setLoading(false)
      setShowDeleteConfirm(false)
      setDeleteCandidate(null)
    }
  }

  if (error) {
    return <InfoMessage style={{ color: 'red' }}>{error}</InfoMessage>
  }

  const getProductActions = useCallback(
    (product: ProductListItem): RowAction[] => [
      { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => handleEdit(product.id) },
      { label: 'Editar', icon: <Pencil size={16} />, onClick: () => handleEdit(product.id) },
      { label: 'Criar Variação', icon: <Copy size={16} />, onClick: () => handleCreateVariation(product.id) },
      { label: 'Gerar PDF', icon: <Download size={16} />, onClick: () => setPdfProductId(product.id) },
      { label: 'Excluir', icon: <Trash2 size={16} />, onClick: () => handleDelete(product.id) },
      {
        key: 'audit',
        label: 'Auditoria',
        icon: <History size={16} color="#2563eb" />,
        onClick: () => {
          setAuditModalData({
            tableName: 'Product',
            recordId: product.id,
            recordTitle: product.name,
          })
        },
      },
    ],
    [handleEdit, handleCreateVariation, handleDelete]
  )

  if (viewMode === 'board' && loading && products.length === 0) {
    return (
      <OrdenoLoading
        variant="inline"
        text="Carregando fichas técnicas..."
        style={{ padding: '4rem 0' }}
      />
    )
  }

  return (
    <>
      {viewMode === 'table' ? (
        <StandardDataTable
          tableName="Product"
          columns={columns}
          data={products}
          columnsState={columnsState}
          onColumnsChange={handleColumnsChange}
          onRestoreColumns={handleRestoreColumns}
          totalCount={totalProducts}
          onLoadMore={() => setVisibleCount((prev) => prev + 50)}
          hasMore={typeof totalProducts === 'number' ? products.length < totalProducts : false}
          isLoading={loading}
          countLabel='produtos'
          exportFileName='fichas-tecnicas'
          rowActions={getProductActions}
        />
      ) : (
        <BoardWrapper>
          <BoardGrid>
            <CreateBoardCard onClick={() => navigate('/panel/composition-product/create')}>
              <PlusCircle size={28} />
              <span>Nova Ficha</span>
            </CreateBoardCard>
            {products.map((product: ProductListItem, index: number) => (
              <BoardCard
                key={product.id}
                onClick={() => navigate(`/panel/composition-product/edit/${product.id}`)}
              >
                <BoardCoverArea
                  $background={BOARD_COVER_PALETTE[index % BOARD_COVER_PALETTE.length]}
                >
                  {product.coverImageUrl ? (
                    <BoardCoverImage
                      src={product.coverImageUrl}
                      alt={product.name}
                      $scale={product.coverImageScale}
                      $posX={product.coverImagePosX}
                      $posY={product.coverImagePosY}
                    />
                  ) : (
                    <BoardCoverFallback>
                      <ImageOff size={34} />
                      <span>Sem capa</span>
                    </BoardCoverFallback>
                  )}
                </BoardCoverArea>

                <BoardInfo>
                  <BoardHeaderRow>
                    <BoardCode>
                      {`Cod. ${product.code}`}
                    </BoardCode>
                    <div onClick={(e) => e.stopPropagation()}>
                      <RowActionsMenu actions={getProductActions(product)} />
                    </div>
                  </BoardHeaderRow>
                  <BoardTitle title={product.name}>{product.name}</BoardTitle>

                  <BoardFooter>
                    {product.collection !== '-' && (
                      <CollectionTagBadge title={product.collection}>
                        {product.collection}
                      </CollectionTagBadge>
                    )}
                    {product.episode !== '-' && (
                      <EpisodeTagBadge title={product.episode}>
                        {product.episode}
                      </EpisodeTagBadge>
                    )}
                    {product.family !== '-' && (
                      <FamilyTagBadge title={product.family}>
                        {product.family}
                      </FamilyTagBadge>
                    )}
                  </BoardFooter>

                  <CardSeparator />

                  <CardInfoContainer>
                    <CardStepRow>
                      <CardStepLabel>Etapa:</CardStepLabel>
                      <CardStepBadge title={product.step && product.step !== '-' ? product.step : 'Sem etapa'}>
                        {product.step && product.step !== '-' ? product.step : 'Sem etapa'}
                      </CardStepBadge>

                      {product.tags && product.tags.length > 0 && (
                        product.tags.map((tag: any) => (
                          <TagComponent key={tag.id} name={tag.name} color={tag.color} />
                        ))
                      )}
                    </CardStepRow>
                  </CardInfoContainer>
                </BoardInfo>
              </BoardCard>
            ))}
          </BoardGrid>

          <LoadMoreButton
            currentCount={products.length}
            totalCount={totalProducts ?? undefined}
            hasMore={typeof totalProducts === 'number' ? products.length < totalProducts : false}
            onLoadMore={() => setVisibleCount((prev) => prev + 50)}
          />
        </BoardWrapper>
      )}

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title="Tem certeza que deseja excluir este produto?"
        message={
          deleteCandidate
            ? `Produto: ${deleteCandidate.name}
              Essa ação não poderá ser desfeita.`
            : 'Essa ação não poderá ser desfeita.'
        }
        actions={[
          {
            label: 'Não, voltar',
            onClick: () => setShowDeleteConfirm(false),
            variant: 'outline',
          },
          {
            label: 'Sim, excluir',
            onClick: handleConfirmDelete,
            variant: 'danger',
          },
        ]}
      />

      {pdfProductId && (
        <GeneratePDFModal
          productId={pdfProductId}
          onClickClose={() => setPdfProductId(null)}
        />
      )}

      {auditModalData && (
        <AuditHistoryModal
          tableName={auditModalData.tableName}
          recordId={auditModalData.recordId}
          recordTitle={auditModalData.recordTitle}
          onClose={() => setAuditModalData(null)}
        />
      )}
    </>
  )
}

export default CompositionTable
